
import {
  defineComponent, onBeforeMount, PropType, reactive, watch,
} from 'vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import Card from '@/core/components/Tela/Card.vue';
import ServicoTipoOperacao from '@/servicos/Cadastros/ServicoTipoOperacao';
import ServicoCenarioFiscal from '@/servicos/Cadastros/Tributacoes/ServicoCenarioFiscal';
import ServicoPessoa from '@/servicos/Cadastros/Pessoas/ServicoPessoa';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { ICteImposto } from '@/models/Entidades/Compras/ConhecimentoTransporte/ICteImposto';

function criarImpostoPadrao(): ICteImposto {
  return {
    valorTotalTributosAproximado: 0,
    icms: {
      cstIcms: '',
      baseCalculo: 0,
      aliquota: 0,
      valorIcms: 0,
      valorBaseCalculoSt: 0,
      valorIcmsSt: 0,
      percentualReducaoBaseCalculo: 0,
    },
    iss: {
      baseCalculo: 0,
      aliquota: 0,
      valorIss: 0,
      codigoMunicipioFatoGerador: 0,
    },
    pis: {
      cstPis: '',
      baseCalculo: 0,
      aliquota: 0,
      valorPis: 0,
    },
    cofins: {
      cstCofins: '',
      baseCalculo: 0,
      aliquota: 0,
      valorCofins: 0,
    },
  };
}

function normalizarImposto(imposto?: Partial<ICteImposto> | null): ICteImposto {
  const impostoPadrao = criarImpostoPadrao();

  return {
    ...impostoPadrao,
    ...(imposto ?? {}),
    icms: {
      ...impostoPadrao.icms,
      ...(imposto?.icms ?? {}),
    },
    iss: {
      ...impostoPadrao.iss,
      ...(imposto?.iss ?? {}),
    },
    pis: {
      ...impostoPadrao.pis,
      ...(imposto?.pis ?? {}),
    },
    cofins: {
      ...impostoPadrao.cofins,
      ...(imposto?.cofins ?? {}),
    },
  };
}

export default defineComponent({
  name: 'CteImpostos',
  props: {
    imposto: {
      type: Object as PropType<ICteImposto | null>,
      default: null,
    },
    empresa: {
      type: Number,
      required: true,
    },
    codigoTipoOperacao: {
      type: Number,
      default: 0,
    },
    codigoPessoa: {
      type: Number,
      default: 0,
    },
    editavel: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    CampoNumerico,
    Card,
  },
  emits: ['update:imposto'],
  setup(props, { emit }) {
    const { apresentarMensagemAlerta, apresentarMensagemErro, apresentarMensagemSucesso } = useTelaBase();
    const servicoTipoOperacao = new ServicoTipoOperacao();
    servicoTipoOperacao.requisicaoSistema();
    const servicoCenarioFiscal = new ServicoCenarioFiscal();
    servicoCenarioFiscal.requisicaoSistema();
    const servicoPessoa = new ServicoPessoa();
    servicoPessoa.requisicaoSistema();

    const state = reactive({
      imposto: normalizarImposto(props.imposto),
    });

    onBeforeMount(() => {
      state.imposto = normalizarImposto(props.imposto);
    });

    watch(() => state.imposto, (novoImposto) => {
      emit('update:imposto', novoImposto);
    }, { deep: true, flush: 'post' });

    function atualizarTotalTributos() {
      state.imposto.valorTotalTributosAproximado = (state.imposto.icms.valorIcms ?? 0)
        + (state.imposto.icms.valorIcmsSt ?? 0)
        + (state.imposto.iss.valorIss ?? 0)
        + (state.imposto.pis.valorPis ?? 0)
        + (state.imposto.cofins.valorCofins ?? 0);
    }

    function calcularValorIcms() {
      const baseCalculo = Number(state.imposto.icms.baseCalculo ?? 0);
      const aliquota = Number(state.imposto.icms.aliquota ?? 0);
      state.imposto.icms.valorIcms = Number(((baseCalculo * aliquota) / 100).toFixed(2));
      atualizarTotalTributos();
    }

    async function aplicarCenarioFiscal() {
      if (!UtilitarioGeral.validaCodigo(props.codigoTipoOperacao)) {
        apresentarMensagemAlerta('Selecione o tipo de operação para aplicar o cenário fiscal.');
        return;
      }

      try {
        const tipoOperacao = await servicoTipoOperacao.obter(props.codigoTipoOperacao, props.empresa);
        if (!UtilitarioGeral.validaCodigo(tipoOperacao.codigoCenarioFiscal)) {
          apresentarMensagemAlerta('O tipo de operação informado não possui cenário fiscal vinculado.');
          return;
        }

        const definicoes = await servicoCenarioFiscal.obterTodasDefinicoes(tipoOperacao.codigoCenarioFiscal, props.empresa);
        if (!UtilitarioGeral.validaLista(definicoes)) {
          apresentarMensagemAlerta('Nenhuma definição fiscal encontrada para o cenário informado.');
          return;
        }

        let siglaUf = '';
        if (UtilitarioGeral.validaCodigo(props.codigoPessoa)) {
          siglaUf = await servicoPessoa.obterSiglaUf(props.codigoPessoa);
        }

        const definicaoSelecionada = definicoes.find((definicao) => definicao.siglaEstado === siglaUf) || definicoes[0];

        if (definicaoSelecionada?.definicaoIcms) {
          state.imposto.icms.cstIcms = definicaoSelecionada.definicaoIcms.cstIcms ?? '';
          state.imposto.icms.aliquota = definicaoSelecionada.definicaoIcms.aliquotaIcms ?? 0;
          state.imposto.icms.percentualReducaoBaseCalculo = definicaoSelecionada.definicaoIcms.reducaoBaseCalculoIcms ?? 0;
        }

        if (definicaoSelecionada?.definicaoPis) {
          state.imposto.pis.cstPis = definicaoSelecionada.definicaoPis.cstPis ?? '';
          state.imposto.pis.aliquota = definicaoSelecionada.definicaoPis.aliquotaPis ?? 0;
        }

        if (definicaoSelecionada?.definicaoCofins) {
          state.imposto.cofins.cstCofins = definicaoSelecionada.definicaoCofins.cstCofins ?? '';
          state.imposto.cofins.aliquota = definicaoSelecionada.definicaoCofins.aliquotaCofins ?? 0;
        }

        atualizarTotalTributos();
        apresentarMensagemSucesso('Cenário fiscal aplicado aos impostos do CT-e.');
      } catch (error) {
        apresentarMensagemErro('Não foi possível aplicar o cenário fiscal do tipo de operação.');
      }
    }

    function formatarNumero(valor?: number) {
      if (valor === undefined || valor === null) {
        return '0,00';
      }
      return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return {
      props,
      state,
      calcularValorIcms,
      atualizarTotalTributos,
      aplicarCenarioFiscal,
      formatarNumero,
    };
  },
});
